import Dashboard from '@/layout/dashboard/wrapper';
import { asArray, isShallowEqual, makeIterator } from '@/utils/data';
import { toBoolean, toNumber } from '@/utils/string';
import { createRouter, createWebHistory, isNavigationFailure } from 'vue-router';
import { reportService } from '../index';
import History from './history';
import { mustConfirmEmail } from '@/middlewares';

export default class Router {
  /**
   * Router constructor.
   */
  constructor() {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

     
    this._router = createRouter({
      history: createWebHistory(),
      scrollBehavior(routeTo, routeFrom, savedPosition) {
        if (savedPosition) {
          return savedPosition;
        }

        let shouldReset = null;

        if (routeTo.meta?.scroll && routeTo.name === routeFrom?.name) {
          if (routeTo.meta.scroll.ignoreQuery) {
            const toQuery = Object.assign({}, routeTo.query);
            const fromQuery = Object.assign({}, routeFrom.query);

            let ignoredChanged = false;

            routeTo.meta.scroll.ignoreQuery.forEach(it => {
              if (toQuery[it] !== fromQuery[it]) {
                ignoredChanged = true;
              }

              delete toQuery[it];
              delete fromQuery[it];
            });

            shouldReset = !(isShallowEqual(toQuery, fromQuery) && ignoredChanged);
          }
        }

        if (shouldReset !== true && routeTo.meta?.scroll && routeTo.name === routeFrom?.name) {
          if (routeTo.meta.scroll.ignoreParams) {
            const toParams = Object.assign({}, routeTo.params);
            const fromParams = Object.assign({}, routeFrom.params);

            let ignoredChanged = false;

            routeTo.meta.scroll.ignoreParams.forEach(it => {
              if (toParams[it] !== fromParams[it]) {
                ignoredChanged = true;
              }

              delete toParams[it];
              delete fromParams[it];
            });

            shouldReset = !(isShallowEqual(toParams, fromParams) && ignoredChanged);
          }
        }

        if (shouldReset === false) {
          return null;
        }

        return { x: 0, y: 0 };
      },
      routes: [
        { path: '/:pathMatch(.*)*', component: require('../../pages/empty').default },
      ],
    });
    this.history = new History(this._router);
    this._tracking = true;
  }

  /**
   * Get current route.
   */
  get current() {
    return this._router.currentRoute;
  }

  /**
   * Add global route.
   * @param routes
   */
  addGlobalRoute(routes) {
    routes.forEach(route => this._router.addRoute(route));
  }

  /**
   * Add dashboard route.
   * @param {Array|*} config
   */
  addProtectedRoute(config) {
    this._router.addRoute({
      path: '/dashboard',
      component: Dashboard,
      meta: {
        auth: {
          required: true,
          redirectGuest: { name: 'login' },
        },
        middleware: [mustConfirmEmail],
      },
      children: asArray(config),
    });
  }

  /**
   * Add dashboard route.
   * @param {Array|*} config
   */
  addOnboardingRoute(config) {
    this._router.addRoute({
      path: '/register-confirmation',
      meta: {
        auth: {
          required: true,
          redirectGuest: { name: 'login' },
        },
      },
      children: asArray(config),
    });
  }

  afterEach(guard) {
    this._router.afterEach(guard);
  }

  /**
   * Add before each router guard.
   * @param {*} guard
   */
  beforeEach(guard) {
    this._router.beforeEach(guard);
  }

  /**
   * Add before each router guard.
   * @param {*} guard
   */
  beforeResolve(guard) {
    this._router.beforeResolve(guard);
  }

  onError(handler) {
    this._router.onError(handler);
  }

  /**
   * Go to new page.
   * @param {*} config
   * @param {boolean} track
   */
  push(config, track = true) {
    this._tracking = track;
    this._router.push(config, () => this.history.resetTracking(), (err) => {
      if (isNavigationFailure(err)) {
        console.warn(err);
      } else {
        console.error(err);
        reportService.report(err);
      }

      this.history.resetTracking();
    });
  }

  /**
   * Replace current page.
   * @param {*} config
   */
  replace(config) {
    this._tracking = false;
    this._router.replace(config, () => this.history.resetTracking(), (err) => {
      if (isNavigationFailure(err)) {
        console.warn(err);
      } else {
        console.error(err);
        reportService.report(err);
      }

      this.history.resetTracking();
    });
  }

  /**
   * Update query object.
   * @param {Object.<String, *>} map
   * @param {Boolean} replace
   */
  updateQuery(map, replace = false) {
    const query = Object.assign({}, this._router.currentRoute.value.query);

    for (const { key: param, value } of makeIterator(map)) {
      if (['', null, undefined].indexOf(value) === -1) {
        query[param] = value;
      } else {
        delete query[param];
      }
    }

    if (replace) {
      this.replace({ query });
    } else {
      this.push({ query });
    }
  }

  /**
   * Get query param or all query params.
   * @param {string|null} name
   * @param {*} def
   * @param {*} type
   * @returns {*}
   */
  input(name = null, def = null, type = null) {
    const route = this.current;
    const query = route ? route.value.query : {};

    if (!name) {
      return Object.assign({}, query);
    }

    if (!query[name]) {
      return def;
    }

    if (type === null) {
      return query[name];
    }

    const value = query[name];
    if (type === Number) {
      return toNumber(value, def);
    } else if (type === Boolean) {
      return toBoolean(value, def);
    }

    return def;
  }

  /**
   * Go back to last unique route.
   * @param {*} defaultConfig
   */
  backToUnique(defaultConfig = null) {
    if (this.history.lastUnique) {
      this._router.push(this.history.lastUnique);
    } else {
      if (defaultConfig) {
        this._router.push(defaultConfig);
      } else {
        this._router.back();
      }
    }
  }
}
